import { datadogRum } from "@datadog/browser-rum";
import { zodResolver } from "@hookform/resolvers/zod";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Button, List, ListItem, Menu, MenuItem, Tab, Tabs, Typography } from "@mui/material";
import Loading from "@notemeal/shared/ui/global/Loading";
import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import SomethingWentWrongDialog from "apps/web/src/componentLibrary/ConfirmationDialog/SomethingWentWrongDialog";
import LoadingButton from "apps/web/src/components/universal/LoadingButton";
import { NAV_ORG_KITCHEN_MENU_BUILDER } from "apps/web/src/pages/Auth/Org/Kitchen/KitchenPaths";
import { PlannedMenuMealInput, useDeletePlannedMenuMutation, useSavePlannedMealsMutation } from "apps/web/src/types";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom-v5-compat";
import { MenuBuilderSchema, MenuBuilderType } from "./Meal/MenuBuilderMealSchema";
import { useMenuBuilderContext } from "./MenuBuilderProvider";
import { MenuBuilderWeek } from "./MenuBuilderWeek";
import { formatWeekLabel } from "./utils";

export const MenuBuilderGrid = () => {
  const navigate = useNavigate();
  const {
    menuId,
    menuName,
    weeks,
    loading,
    selectedWeekIndex,
    setSelectedWeekIndex,
    mealsToDelete,
    setMealsToDelete,
    rowsToDelete,
    setRowsToDelete,
    rowItemsToDelete,
    setRowItemsToDelete,
  } = useMenuBuilderContext();

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteDialogueOpen, setDeleteDialogueOpen] = useState<boolean>(false);
  const [errorWhileSavingDialog, setErrorWhileSavingDialog] = useState<boolean>(false);
  const [weeksWithErrors, setWeeksWithErrors] = useState<number[]>([]);
  const [savePlannedMeals, { loading: loadingSavePlannedMeals }] = useSavePlannedMealsMutation({
    refetchQueries: ["PlannedMenuGrid"],
    onError: e => {
      setErrorWhileSavingDialog(true);
      console.error(e);
    },
    onCompleted: () => {
      datadogRum.addAction("menu_builder.saved_menu", { menuId });
    },
  });

  const [deletePlannedMenuMutation] = useDeletePlannedMenuMutation({
    onCompleted: () => {
      setDeleteDialogueOpen(false);
      navigate(NAV_ORG_KITCHEN_MENU_BUILDER, { state: { deletedMenuName: menuName } });
    },
  });

  const form = useForm<MenuBuilderType>({
    resolver: zodResolver(MenuBuilderSchema),
    defaultValues: { weeks: [] },
  });

  if (loading) {
    return (
      <Box sx={{ height: "100%" }}>
        <Loading progressSize="lg" />
      </Box>
    );
  }

  const weekLabels = weeks.map((week, index) => formatWeekLabel(week, index));

  // manually handle validation and submission because we want to skip validation on empty meals
  const handleSave = async () => {
    datadogRum.addAction("menu_builder.clicked_save", { menuId });
    const data = form.getValues();

    // using forEach to avoid needing a type assertion with map
    const dirtiedFields: `weeks.${number}.meals.${number}`[] = [];
    data.weeks.forEach((week, weekIndex) => {
      week.meals.forEach((meal, mealIndex) => {
        if (form.getFieldState(`weeks.${weekIndex}.meals.${mealIndex}`).isDirty) {
          dirtiedFields.push(`weeks.${weekIndex}.meals.${mealIndex}`);
        } else {
          const hasDirtyRows = meal.rows.some((_, index) => {
            if (form.getFieldState(`weeks.${weekIndex}.meals.${mealIndex}.rows.${index}`).isDirty) {
              return true;
            }
            const dirtyRowItems = form.getValues(`weeks.${weekIndex}.meals.${mealIndex}.rows.${index}.items`).some(item => item.id === "");
            return dirtyRowItems;
          });
          if (hasDirtyRows) {
            dirtiedFields.push(`weeks.${weekIndex}.meals.${mealIndex}`);
          }
        }
      });
    });
    if (await form.trigger(dirtiedFields)) {
      const mealsToUpsert: PlannedMenuMealInput[] = dirtiedFields.map(formId => {
        const meal = form.getValues(formId);
        return {
          id: meal.id,
          name: meal.mealName,
          type: meal.mealType,
          startTime: meal.startTime,
          endTime: meal.endTime,
          plannedMenuWeekId: meal.weekId,
          plannedMenuMealRows: meal.rows.map(row => ({
            id: row.id,
            position: row.position,
            diningStationName: row.diningStationName,
            foodType: row.foodType,
            plannedMenuMealRowItems: row.items.map(item => ({
              id: item.id,
              dayOfWeek: item.dayOfWeek,
              position: item.position,
              menuItemId: item.menuItem.id,
            })),
          })),
        };
      });
      if (mealsToUpsert.length > 0 || mealsToDelete.length > 0 || rowsToDelete.length > 0 || rowItemsToDelete.length > 0) {
        await savePlannedMeals({
          variables: {
            input: {
              plannedMenuId: menuId,
              mealsToDelete,
              rowsToDelete,
              rowItemsToDelete,
              mealsToUpsert,
            },
          },
        });
        // set form to not dirty, but don't unmount everything
        form.reset({}, { keepValues: true });
        setMealsToDelete([]);
        setRowsToDelete([]);
        setRowItemsToDelete([]);
      }
    } else {
      // failed validation, check if there are errors in other week tabs
      const errors = form.formState.errors.weeks;
      if (Array.isArray(errors)) {
        const errorIndices = errors.reduce((indices, hasError, index) => (hasError ? [...indices, index] : null), []);
        datadogRum.addAction("menu_builder.save_failed_validation", { menuId, errors });
        if (errorIndices.length > 1 || errorIndices[0] !== selectedWeekIndex) {
          setWeeksWithErrors(errorIndices);
        }
      }
    }
  };
  return (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: "auto",
        gridTemplateRows: "auto 1fr",
        overflow: "auto",
      }}
    >
      <Box sx={{ py: 3, display: "flex", justifyContent: "space-between" }}>
        <Tabs
          sx={{ height: 35 }}
          value={selectedWeekIndex}
          onChange={(e, value) => setSelectedWeekIndex(value)}
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
        >
          {weeks.map((week, index) => {
            return (
              <Tab
                key={week.id}
                sx={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
                label={weekLabels[index]}
              />
            );
          })}
        </Tabs>
        <Box sx={{ gap: 1, display: "flex", pt: 1, pb: 1 }}>
          <LoadingButton
            onClick={handleSave}
            buttonText={"Save"}
            loading={loadingSavePlannedMeals} />
          <Button
            onClick={e => {
              setMenuAnchorEl(e.currentTarget);
            }}
            sx={{
              p: 0.5,
              whiteSpace: "nowrap",
              minWidth: "auto",
              "& .MuiButton-startIcon": {
                m: 0,
              },
            }}
            variant="outlined"
          >
            <MoreVertIcon />
          </Button>

          <Menu
            anchorEl={menuAnchorEl}
            onClose={() => setMenuAnchorEl(null)}
            open={menuAnchorEl !== null}>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null);
                setDeleteDialogueOpen(true);
              }}
            >
              <Typography variant="body1">Delete</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <Box sx={{ overflow: "auto" }}>
        {weeks.map((week, index) => {
          return <MenuBuilderWeek
            key={week.id}
            weekFormIndex={index}
            weekId={week.id}
            form={form} />;
        })}
      </Box>

      <ConfirmationDialog
        open={deleteDialogueOpen}
        title="Delete Menu"
        message={`Are you sure you want to delete "${menuName}"? This action cannot be undone.`}
        onCancel={() => {
          datadogRum.addAction("menu_builder.cancelled_delete_menu", { menuId });
          setDeleteDialogueOpen(false);
        }}
        onConfirm={() => {
          datadogRum.addAction("menu_builder.deleted_menu", { menuId });
          deletePlannedMenuMutation({ variables: { input: { id: menuId } } });
        }}
        variant="containedDestructive"
      />

      <ConfirmationDialog
        open={weeksWithErrors.length > 0}
        title="Unable to Save Menu"
        message={
          <Box>
            <Typography>
              The menu cannot be saved due to unresolved issues. Please check and fix the errors on the following weeks in order to proceed:
            </Typography>
            <List sx={{ listStyleType: "disc", pl: 3, pb: 0 }}>
              {weeksWithErrors.map(weekIndex => (
                <ListItem sx={{ display: "list-item", p: 0 }}>{weekLabels[weekIndex]}</ListItem>
              ))}
            </List>
          </Box>
        }
        onCancel={() => setWeeksWithErrors([])}
        onConfirm={() => setWeeksWithErrors([])}
      />

      <SomethingWentWrongDialog
        onClose={() => setErrorWhileSavingDialog(false)}
        open={errorWhileSavingDialog}
        loading={loading}
        content={"We were unable to save the menu due to internal server errors. Please try again by clicking on “Save”."}
      />
    </Box>
  );
};
